/**
 * Module for saving and loading pretahovani (drag-and-drop) data
 * Stores the state of draggable items (which items were dropped/used)
 */
export default class PretahovaniSave {
  static getNodeTextColorClass(element) {
    if (!element || !element.classList) return null;

    const colorClass = Array.from(element.classList).find((className) =>
      className.startsWith('node-text-'),
    );

    return colorClass || null;
  }

  static applyNodeTextColorClass(element, color) {
    if (!element || !element.classList) return;

    Array.from(element.classList).forEach((className) => {
      if (className.startsWith('node-text-')) {
        element.classList.remove(className);
      }
    });

    if (color) {
      element.classList.add(`node-text-${color}`);
    }
  }

  /**
   * Extract draggable items state from DOM
   * @param {HTMLElement} container - The draggable container element
   * @returns {Array} Array of item states
   */
  static getData(container) {
    const items = Array.from(
      container.querySelectorAll('.draggable__item[data-id]'),
    );

    return items.map((item) => {
      const dataId = item.getAttribute('data-id');
      const wasDropped = item.classList.contains('was-dropped');
      const medium = item.getAttribute('data-draggable-item') || null;

      // Extract content based on medium type
      let content = null;
      switch (medium) {
        case 'tag': {
          const span = item.querySelector('.keywords-item span');
          content = span ? span.textContent.trim() : null;

          const coloredNode = item.querySelector('.keywords-item');
          const colorClass = PretahovaniSave.getNodeTextColorClass(coloredNode);
          const color = colorClass
            ? colorClass.replace('node-text-', '')
            : null;

          return {
            dataId,
            medium,
            content,
            color,
            wasDropped,
          };
        }
        case 'image': {
          const img = item.querySelector('img');
          content = img
            ? img.getAttribute('src').replace('../assets/img/', '')
            : null;
          break;
        }
        case 'user-text': {
          const textarea = item.querySelector('textarea');
          content = textarea ? textarea.value : null;
          break;
        }
        case 'svg': {
          const svgId = item.getAttribute('data-svg-id');
          const svgImage = item.getAttribute('data-svg-image');
          if (svgId && svgImage) {
            content = { id: svgId, soubor: svgImage };
          } else {
            // Fallback: try to extract from rendered SVG
            const svg = item.querySelector('svg');
            if (svg) {
              const image = svg.querySelector('image');
              const href = image ? image.getAttribute('xlink:href') : null;
              content = {
                id: svg.id.replace('-svg', ''),
                soubor: href ? href.replace('../assets/img/', '') : null,
              };
            } else {
              content = null;
            }
          }
          break;
        }
        case 'audio': {
          const audio = item.querySelector('audio source');
          content = audio
            ? audio.getAttribute('src').replace('../assets/audio/', '')
            : null;
          break;
        }
        case 'video': {
          const video = item.querySelector('video source');
          content = video
            ? video.getAttribute('src').replace('../assets/video/', '')
            : null;
          break;
        }
        case 'text': {
          const span = item.querySelector('.text__item span');
          content = span ? span.innerHTML : null;
          break;
        }
        default:
          content = null;
      }

      return {
        dataId,
        medium,
        content,
        wasDropped,
      };
    });
  }

  /**
   * Restore draggable items state to DOM
   * @param {HTMLElement} container - The draggable container element
   * @param {Array} items - Array of item states
   */
  static loadData(container, items) {
    console.log('items: ', items);
    if (!items || !Array.isArray(items)) return;

    const createMissingTagItem = (itemData) => {
      if (itemData.medium !== 'tag' || !itemData.content) {
        return null;
      }

      const template = container.querySelector(
        '.draggable__item.hidden[data-draggable-item="tag"]',
      );

      if (!template) {
        return null;
      }

      const clone = template.cloneNode(true);
      const textSpan = clone.querySelector('.keywords-item span');

      clone.setAttribute('data-id', itemData.dataId);
      clone.classList.remove('hidden', 'draggable__item--placeholder');
      clone.classList.remove('is-dragged', 'dropped__item');

      if (textSpan) {
        textSpan.textContent = itemData.content;
      }

      const color =
        itemData.color;
      const inner = clone.firstElementChild;
      const keywordsItem = clone.querySelector('.keywords-item');
      PretahovaniSave.applyNodeTextColorClass(inner, color);
      PretahovaniSave.applyNodeTextColorClass(keywordsItem, color);

      container.appendChild(clone);
      return clone;
    };

    items.forEach((itemData) => {
      let item = container.querySelector(
        `.draggable__item[data-id="${itemData.dataId}"]`,
      );

      // Tags duplicated from text editor do not exist in preview mode,
      // so recreate them from saved structured data.
      if (!item) {
        item = createMissingTagItem(itemData);
      }

      if (item) {
        // Restore was-dropped state
        if (itemData.wasDropped) {
          item.classList.add('was-dropped');
        } else {
          item.classList.remove('was-dropped');
        }
      }
    });
  }
}
